import { useParams, Link } from 'react-router-dom'
import { useSport } from '@/hooks/useSport'
import { useApi } from '@/hooks/useApi'
import { fetchTeam } from '@/lib/api'
import { TeamAvatar } from '@/components/ui/TeamAvatar'
import { RankTile } from '@/components/ui/RankTile'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { Skeleton } from '@/components/ui/Skeleton'
import { SectionHeader } from '@/components/ui/SectionHeader'
import clsx from 'clsx'

export default function TeamPage() {
  const { sport } = useSport()
  const { slug } = useParams<{ slug: string }>()
  const { data, loading, error, errorStatus } = useApi(
    () => slug ? fetchTeam(sport, slug) : Promise.reject('No slug'),
    [sport, slug],
  )

  if (error) {
    // A 404 means the team genuinely doesn't exist; anything else is a server
    // error and should not be mislabelled as "not found".
    const notFound = errorStatus === 404
    return (
      <div className="card p-8 text-center">
        <p className="text-red-400 font-medium">
          {notFound ? 'Team not found' : 'Something went wrong loading this team'}
        </p>
        <Link to={`/${sport}/rpi`} className="text-sm text-accent mt-2 inline-block hover:underline">
          Browse all teams
        </Link>
      </div>
    )
  }

  if (loading || !data) return (
    <div className="space-y-6">
      <Skeleton variant="card" className="h-48" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} variant="card" className="h-24" />)}
      </div>
    </div>
  )

  const { team, rpi, ranking, record, recent_games } = data
  const hasRecord = record && record.games_played > 0
  const topRank = rpi ?? ranking

  return (
    <div className="space-y-8">
      {/* Hero banner */}
      <div className="card-elevated p-6 sm:p-8">
        <div className="flex flex-col items-center gap-5 text-center">
          <TeamAvatar name={team.name} logoUrl={team.logo_url} colorSeed={team.conference_name} size="lg" />
          <div>
            <h1 className="text-3xl font-bold text-white">{team.name}</h1>
            <p className="text-slate-400 mt-1">
              {team.conference_name}
              {hasRecord && (
                <>
                  <span className="mx-2 text-slate-600">·</span>
                  <span className="font-mono text-slate-300" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {record.display}
                  </span>
                </>
              )}
            </p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
              <Link to={`/schools/${team.slug}`} className="text-sm text-accent hover:underline">
                All {team.name} teams →
              </Link>
              {team.camp_url && (
                <a href={team.camp_url} target="_blank" rel="noopener noreferrer" className="text-sm text-accent hover:underline">
                  Soccer camps ↗
                </a>
              )}
              {team.athletic_department_url && (
                <a href={team.athletic_department_url} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 hover:text-accent hover:underline">
                  Athletics site ↗
                </a>
              )}
              {team.organization_url && (
                <a href={team.organization_url} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 hover:text-accent hover:underline">
                  School site ↗
                </a>
              )}
            </div>
          </div>
          {topRank && (
            <RankTile
              rank={topRank.rank}
              prevRank={topRank.prev_rank}
              label={rpi ? 'RPI' : 'Coaches Poll'}
            />
          )}
        </div>
      </div>

      {/* Stat cards */}
      {rpi ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="RPI Rank" value={`#${rpi.rank}`} highlight />
          <StatCard label="Overall Record" value={rpi.record_overall} />
          <StatCard label="Conference" value={rpi.record_conf} />
          <StatCard label="Previous Rank" value={rpi.prev_rank ? `#${rpi.prev_rank}` : 'N/A'} />
        </div>
      ) : hasRecord && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Record" value={record.display} highlight />
          <StatCard label="Wins" value={String(record.wins)} />
          <StatCard label="Losses" value={String(record.losses)} />
          <StatCard label="Draws" value={String(record.draws)} />
        </div>
      )}

      {/* Recent Games */}
      <section>
        <SectionHeader title="Recent Games" />
        <div className="space-y-2">
          {recent_games?.map((g) => {
            const isFinal = g.status?.toLowerCase() === 'final'
            const isHomeTeam = g.home_team_slug ? g.home_team_slug === team.slug : g.home_team_name === team.name
            const teamScore = isHomeTeam ? g.home_score : g.away_score
            const oppScore = isHomeTeam ? g.away_score : g.home_score
            const opponentName = isHomeTeam ? g.away_team_name : g.home_team_name
            const opponentSlug = isHomeTeam ? g.away_team_slug : g.home_team_slug
            const opponentLogoUrl = isHomeTeam ? g.away_team_logo_url : g.home_team_logo_url
            const won = isFinal && teamScore != null && oppScore != null && teamScore > oppScore
            const lost = isFinal && teamScore != null && oppScore != null && teamScore < oppScore

            return (
              <div key={g.id} className={clsx(
                'flex items-center justify-between p-3 rounded-lg border-l-4 transition-colors hover:bg-pitch-800/40',
                won && 'border-l-zone-safe',
                lost && 'border-l-zone-out',
                !won && !lost && 'border-l-pitch-700',
              )}>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-500 w-16">
                    {g.starts_at ? new Date(g.starts_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                  </span>
                  <span className="text-xs text-slate-500 w-8">{isHomeTeam ? 'vs' : '@'}</span>
                  <TeamAvatar name={opponentName || '?'} logoUrl={opponentLogoUrl} size="sm" />
                  {opponentSlug ? (
                    <Link
                      to={`/${sport}/teams/${opponentSlug}`}
                      className="text-sm hover:text-accent transition-colors"
                    >
                      {opponentName}
                    </Link>
                  ) : (
                    <span className="text-sm text-slate-300">{opponentName}</span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {won && <span className="text-zone-safe font-bold">W</span>}
                    {lost && <span className="text-zone-out font-bold">L</span>}
                    {!won && !lost && isFinal && <span className="text-slate-400">D</span>}
                    {isFinal && <span className="text-slate-300 ml-1">{teamScore}-{oppScore}</span>}
                  </span>
                  <StatusBadge status={g.status} />
                </div>
              </div>
            )
          })}
          {!recent_games?.length && (
            <p className="text-sm text-slate-500 p-3">No games recorded.</p>
          )}
        </div>
      </section>
    </div>
  )
}

function StatCard({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={clsx('card p-4 text-center', highlight && 'border-accent/30')}>
      <div className={clsx('text-2xl font-bold', highlight ? 'text-accent' : 'text-white')} style={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
      <div className="text-xs text-slate-500 mt-1.5 uppercase tracking-wide">{label}</div>
    </div>
  )
}
